/**
 * Free-shipping country gate — CLIENT half (D-25 QA follow-up to L3).
 *
 * The server half (snippets/free-shipping-country-gate.liquid) gates every
 * free-shipping promise surface to DE/AT/IT/FR/NL at render time, and a
 * debug-marker probe (2026-08-11) showed Section Rendering API fragments DO
 * localize by the request path's market (/es-es?section_id=cart-drawer
 * renders country=ES, meter absent — fresh and DE-first browser contexts,
 * with and without a conflicting root-scoped localization cookie). But the
 * drawer swaps in whatever fragment the session's fetch returns, and a
 * fragment produced under polluted session state (a lost/leaked preview
 * cookie answers the fetch from the published theme, which has no gate;
 * QA reproduced the ES drawer re-acquiring the meter this way) carries the
 * gated blocks into ungated markets. Belt-and-braces: after EVERY swap of
 * the drawer's DOM, ungated countries lose the gated blocks again, whatever
 * the fragment said. Country signal: window.Shopify.country (standard
 * storefront global, verified in-page per market path), falling back to the
 * drawer's server-rendered data-country; with neither, do nothing (the
 * server render is already correct). DOM-only — no cart reads/writes (R-01).
 */
const FREE_SHIPPING_GATE_COUNTRIES = ['DE', 'AT', 'IT', 'FR', 'NL', 'CH', 'LI', 'ES', 'BE', 'LU'];
const CART_DRAWER_CHECKOUT_CONTEXT_TIMEOUT_MS = 1500;

function waitForCartDrawerTrackingContext() {
  const write = window.shekoTracking?.persistCartContext?.();
  if (!write || typeof write.then !== 'function') return Promise.resolve(false);
  return Promise.race([
    write,
    new Promise((resolve) => setTimeout(() => resolve(false), CART_DRAWER_CHECKOUT_CONTEXT_TIMEOUT_MS)),
  ]);
}

async function handleCartDrawerCheckout(event) {
  const checkoutButton = event.target.closest?.('#CartDrawer-Checkout');
  if (!checkoutButton || checkoutButton.dataset.trackingCheckoutReady === 'true') return;
  event.preventDefault();
  document.dispatchEvent(new CustomEvent('checkout_click', { bubbles: true }));
  await waitForCartDrawerTrackingContext();
  checkoutButton.dataset.trackingCheckoutReady = 'true';
  // Dawn/app listeners can cancel a programmatic requestSubmit(). The AJAX
  // cart is already authoritative and the attribution write has settled,
  // so use Shopify's deterministic checkout route directly.
  window.location.assign('/checkout');
}

// Document capture deliberately sits outside the custom-element constructor:
// it survives element upgrade timing, every drawer innerHTML replacement and
// app listeners installed on the drawer/button itself.
document.addEventListener('click', handleCartDrawerCheckout, true);

/**
 * The drawer's stylesheets load without blocking the first paint (media="print"
 * until their onload flips them; layout/theme.liquid and
 * snippets/cart-drawer.liquid mark them data-cart-drawer-css). The drawer is
 * closed at first paint, but it can be opened before they have landed: by the
 * /cart -> ?drawer=open bounce as soon as this deferred script runs, or by an
 * early cart-icon tap or add to cart. open() waits for them, capped so a
 * failed sheet cannot keep the drawer shut.
 */
const DRAWER_STYLES_TIMEOUT_MS = 1500;
const PENDING_DRAWER_STYLES = 'link[data-cart-drawer-css][media="print"]';

function waitForDrawerStyles() {
  const pending = Array.from(document.querySelectorAll(PENDING_DRAWER_STYLES));
  if (!pending.length) return Promise.resolve();
  return new Promise((resolve) => {
    let remaining = pending.length;
    const settle = () => {
      remaining -= 1;
      if (remaining === 0) resolve();
    };
    pending.forEach((link) => {
      link.addEventListener('load', settle, { once: true });
      link.addEventListener('error', settle, { once: true });
    });
    setTimeout(resolve, DRAWER_STYLES_TIMEOUT_MS);
  });
}

function enforceFreeShippingCountryGate(drawer) {
  const country = (window.Shopify && window.Shopify.country) || drawer.dataset.country || '';
  const ukEligible = country === 'GB' && drawer.dataset.country === 'GB' && drawer.dataset.freeShippingEligible === 'true';
  if (!country || ukEligible || FREE_SHIPPING_GATE_COUNTRIES.indexOf(country) !== -1) return;
  drawer.querySelectorAll('cart-progress').forEach((meter) => meter.remove());
  drawer.querySelectorAll('.cart-drawer__shipping-saving').forEach((saving) => saving.remove());
  // 2026-08-13 (owner: ES shows its fee but never the promise): ungated
  // countries may now carry an HONEST per-country fee estimate (digits,
  // e.g. "4,90 €") — keep it. Replace promise words ("gratis") left by
  // a polluted fragment with the checkout fallback; the saving is removed, so a
  // promise render has no digits left. The EMPTY drawer's "gratis"
  // (nothing to ship) is correct everywhere and stays.
  if (!drawer.classList.contains('is-empty')) {
    drawer.querySelectorAll('.cart-drawer__shipping .totals__price').forEach((fee) => {
      if (fee.dataset.shippingEstimate === 'checkout') return;
      if (fee.textContent.trim() !== '' && !/\d/.test(fee.textContent)) {
        fee.textContent = drawer.dataset.shippingFallback || '';
        fee.dataset.shippingEstimate = 'checkout';
      }
    });
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Overlay close is DELEGATED, deliberately: #CartDrawer-Overlay sits inside
    // #CartDrawer, whose innerHTML is replaced by refreshDrawerForLocale() on
    // market-prefixed routes (and by renderContents() after cart updates). A
    // direct listener on the overlay node dies with the swap — measured on
    // market desktops as "the drawer cannot be closed" (owner report,
    // 2026-08-10). Delegation on the custom element survives every swap.
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.setHeaderCartIconAccessibility();
    this.reconcileColdLoadCartState();

    // Dawn reports only the auxiliary checkout click. Shopify Customer Events
    // owns begin_checkout from checkout_started, so this cannot create a
    // second GA4 funnel event. Delegation survives renderContents() swaps.
    // Live behaviour: /cart bounces to `<root>?drawer=open` (cart-redirect.js)
    // and the drawer opens here. Deferred until <cart-drawer-items> is
    // defined (QA-P1 item 5): the constructor runs during THIS element's
    // upgrade, 19 lines before cart-drawer-items' own define at the bottom
    // of this file - with a non-empty cart the un-upgraded element had no
    // dispatchViewEvent yet, the TypeError aborted openDrawerElement() and
    // the cart-drawer-opened event (GA4 view_cart, O-10) never fired.
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('drawer') === 'open') {
      customElements.whenDefined('cart-drawer-items').then(() => this.openDrawer());
    }

    // Country-gate client half: an OBSERVER, not per-call-site hooks — the
    // drawer's DOM is swapped by four code paths (refreshDrawerForLocale and
    // renderContents here; CartItems.updateQuantity and CartItems.onCartUpdate
    // in cart.js) and only two live in this file. The enforcement is
    // idempotent, so the observer settling after its own removals terminates
    // immediately; on gated countries it early-returns and never mutates.
    enforceFreeShippingCountryGate(this);
    new MutationObserver(() => enforceFreeShippingCountryGate(this)).observe(this, {
      childList: true,
      subtree: true,
    });
  }

  openDrawer() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      this.open(cartLink);
    }
  }

  // D-21 item 57 + D-28 critic blocker: the FIRST cold document load after an
  // AJAX add can be served with stale empty-cart HTML (reproduced 2026-08-12
  // on a cold /collections/abnehm-shakes load with one item in the cart: the
  // server HTML carried icon-cart-empty and no count bubble). Nothing on a
  // cold load re-renders either surface, so the stale state stuck until the
  // next cart mutation — and the first fix patched only the ICON-BUBBLE,
  // leaving the DRAWER stale (bubble "1", drawer opens is-empty with 1 item
  // in the cart; critic's rendered repro). Reconcile once per load: read the
  // cart (one GET /cart.js — reads and section GETs only, never a mutation,
  // R-01) and re-fetch each section only when ITS rendered state disagrees.
  // No loops: nothing here re-invokes the reconcile, and the patch is a plain
  // DOM swap — it does NOT open the drawer and does NOT dispatch
  // cart-drawer-opened, so the tracking contract's view_cart (fires on OPEN)
  // cannot fire from a background patch. The D-25 country-gate
  // MutationObserver sees the swap and re-enforces — idempotent, terminates.
  // Empty guards match the hardened swap sites: a missing or empty payload
  // leaves the existing markup alone.
  reconcileColdLoadCartState() {
    fetch(`${routes.cart_url}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart) return;
        const actualNonEmpty = cart.item_count > 0;
        const patches = [];

        const zone = document.getElementById('cart-icon-bubble');
        if (zone && !!zone.querySelector('.cart-count-bubble') !== actualNonEmpty) {
          patches.push(
            fetch(`${window.location.pathname}?section_id=cart-icon-bubble`)
              .then((response) => (response.ok ? response.text() : null))
              .then((html) => {
                if (!html) return;
                const newInnerHTML = new DOMParser()
                  .parseFromString(html, 'text/html')
                  .querySelector('.shopify-section')?.innerHTML;
                if (newInnerHTML) zone.innerHTML = newInnerHTML;
              }),
          );
        }

        // Drawer half: the rendered emptiness signal is the is-empty class on
        // this element (snippets/cart-drawer.liquid line 108; all of
        // component-cart-drawer.css keys off cart-drawer.is-empty).
        if (this.classList.contains('is-empty') === actualNonEmpty) {
          patches.push(this.reconcileDrawerContents(actualNonEmpty));
        }

        return Promise.all(patches);
      })
      .catch(() => {});
  }

  // Re-fetch the drawer section against the current path (locale-correct, the
  // same Section Rendering API request refreshDrawerForLocale uses) and patch
  // #CartDrawer's children — the same swap surface as every other drawer
  // update, so the outer element's data-country survives. The is-empty class
  // is synced from the fresh fragment (falling back to the /cart.js read that
  // triggered the patch) AFTER a successful swap only.
  reconcileDrawerContents(actualNonEmpty) {
    return fetch(`${window.location.pathname}?section_id=cart-drawer`)
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const sourceDrawer = doc.getElementById('CartDrawer');
        const targetDrawer = this.querySelector('#CartDrawer');
        if (!sourceDrawer || !targetDrawer || !sourceDrawer.innerHTML) return;
        targetDrawer.innerHTML = sourceDrawer.innerHTML;
        const sourceElement = doc.querySelector('cart-drawer');
        const nowEmpty = sourceElement ? sourceElement.classList.contains('is-empty') : !actualNonEmpty;
        this.classList.toggle('is-empty', nowEmpty);
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy, stylesSettled = false) {
    if (this.classList.contains('active')) return;
    if (!stylesSettled && !this.drawerStylesSettled && document.querySelector(PENDING_DRAWER_STYLES)) {
      // Opened before the drawer's stylesheets applied: open once they have,
      // or after the cap even if one failed (one wait, however often open()
      // is called meanwhile). Settled once per page: a sheet that failed keeps
      // media="print" for good and must not delay every later open.
      this.pendingTrigger = triggeredBy || this.pendingTrigger;
      if (!this.stylesWait) {
        this.stylesWait = waitForDrawerStyles().then(() => {
          const trigger = this.pendingTrigger;
          this.stylesWait = null;
          this.pendingTrigger = null;
          this.drawerStylesSettled = true;
          this.open(trigger, true);
        });
      }
      return;
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // Live behaviour, kept: on locale-prefixed paths the drawer body is
    // re-fetched through the Section Rendering API against the CURRENT path
    // before opening, so a stale drawer (cart changed in another tab, cached
    // page HTML) opens fresh and in the right locale. The primary (unprefixed)
    // locale skips the round-trip, exactly as live does.
    const path = window.location.pathname;
    const firstSegment = path.split('/').filter(Boolean)[0];
    const isLocalizedPath =
      firstSegment && (firstSegment.length === 2 || (firstSegment.length <= 6 && firstSegment.includes('-')));
    if (isLocalizedPath) {
      this.refreshDrawerForLocale(path)
        .then(() => this.openDrawerElement())
        .catch(() => this.openDrawerElement());
    } else {
      this.openDrawerElement();
    }
  }

  openDrawerElement() {
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    // Optional-call guard (quick-add.js:42 pattern): the element can exist
    // before its upgrade, in which case the method does not exist yet.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();

    // view_cart for P13 (O-10 fix): live's GA4 view_cart listened for this
    // exact event name and nothing ever dispatched it.
    document.dispatchEvent(new CustomEvent('cart-drawer-opened', { bubbles: true }));
  }

  refreshDrawerForLocale(path) {
    const url = `${path}${path.indexOf('?') >= 0 ? '&' : '?'}section_id=cart-drawer`;
    return fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const sourceDrawer = doc.getElementById('CartDrawer');
        const targetDrawer = this.querySelector('#CartDrawer');
        if (sourceDrawer && targetDrawer && sourceDrawer.innerHTML) {
          targetDrawer.innerHTML = sourceDrawer.innerHTML;
        }
      });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (!parsedState) return;
    if (!parsedState.sections) {
      setTimeout(() => this.open(), 0);
      return;
    }

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const sectionHtml = parsedState.sections[section.id];
      if (!sectionHtml) return;

      const newInnerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (newInnerHTML) sectionElement.innerHTML = newInnerHTML;
    });

    setTimeout(() => {
      // Overlay close is delegated in the constructor — no per-swap re-bind
      // (the old re-bind here also stacked a duplicate listener per update).
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
